/**
 * ignoreSupabaseError — the ONLY sanctioned way to not care whether a Supabase
 * write succeeded.
 *
 * `local/no-unchecked-supabase-error` fails the build on a query whose `error`
 * nobody reads, because three separate four-month data losses across two
 * products all had that one shape. Some calls genuinely do not care — a
 * best-effort analytics ping, a cleanup whose failure changes nothing for the
 * user. Those are legitimate, and the point of this helper is that they have to
 * SAY they are legitimate:
 *
 *     ignoreSupabaseError(
 *       supabase.from("page_views").insert({ path }),
 *       "best-effort analytics; a lost row changes nothing the user sees",
 *     );
 *
 * The reason is a required argument, so an ignored failure is a decision
 * somebody wrote down rather than a line that merely looks finished.
 *
 * NEVER reach for this on a path that persists something a user typed. If the
 * user is going to be told "saved", the write has to have actually saved.
 */
export function ignoreSupabaseError<T>(
  query: PromiseLike<T>,
  reason: string,
): void {
  if (!reason || reason.trim().length < 8) {
    // A blank or one-word reason defeats the whole purpose of the helper.
    throw new Error("ignoreSupabaseError needs a real reason, not a placeholder");
  }
  void Promise.resolve(query).then(
    (res) => {
      const err = (res as { error?: unknown } | null)?.error;
      // Not silent — just not fatal. Someone reading the console can still see
      // it, which is strictly more than the pattern this replaces offered.
      if (err) console.debug("[supabase: ignored on purpose]", reason, err);
    },
    (e) => console.debug("[supabase: ignored on purpose]", reason, e),
  );
}

/**
 * A human-readable sentence for a failed Supabase write, safe to put in a
 * toast. Deliberately does NOT leak the table name, the column, the PostgREST
 * code or any other infrastructure detail — FOX-BIBLE §XXIII — while still
 * telling the user the true thing: it did not save.
 */
export function saveFailureMessage(action = "save that"): string {
  return `We could not ${action}. Nothing was stored — please try again.`;
}
